import React, { useEffect, useState } from 'react';
import { Upload, FileText, AlertTriangle, CheckCircle, XCircle, ChevronDown, ChevronUp, Copy, Mail, MessageCircle } from 'lucide-react';
import { logSlopEvent } from './statsig';

const AISlopDetector = () => {
  const [file, setFile] = useState(null);
  const [textInput, setTextInput] = useState('');
  const [inputMode, setInputMode] = useState('text');
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [error, setError] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);


  const getSlopLevel = (score) => {
    if (score <= 3) return { level: 'Low Slop', message: 'Good job!', color: 'text-green-600', bgColor: 'bg-green-50' };
    if (score <= 6) return { level: 'Medium Slop', message: 'Remember to read your work!', color: 'text-yellow-600', bgColor: 'bg-yellow-50' };
    return { level: 'High Slop', message: 'Too much bot, not enough human', color: 'text-red-600', bgColor: 'bg-red-50' };
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'text-red-600 bg-red-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'low': return 'text-blue-600 bg-blue-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const deriveFriendlyError = (rawMessage) => {
    if (!rawMessage) {
      return 'Something broke mid-slop. Please try again in a moment.';
    }

    const msg = rawMessage.toLowerCase();

    if (
      msg.includes('credit') ||
      msg.includes('quota') ||
      msg.includes('payment') ||
      msg.includes('insufficient') ||
      msg.includes('rate limit')
    ) {
      return "Oops, too popular. I've got to buy some more tokens. Gotta use AI to fight that AI slop, I guess.";
    }

    if (msg.includes('timeout') || msg.includes('timed out')) {
      return 'The slop inspector timed out. Give it another shot in a few seconds.';
    }

    if (msg.includes('network') || msg.includes('fetch')) {
      return 'Network gremlins got in the way. Please check your connection and try again.';
    }

    return rawMessage;
  };

  const parseAnthropicError = async (response) => {
    const statusLine = `${response.status} ${response.statusText}`.trim();
    let message = '';

    try {
      const text = await response.text();

      if (text) {
        try {
          const json = JSON.parse(text);
          message = json?.error?.message || json?.message || text;
        } catch (jsonErr) {
          message = text;
        }
      }
    } catch (readErr) {
      // Ignore parsing issues, fall back to status line
    }

    if (!message) {
      message = `API request failed: ${statusLine}`;
    }

    throw new Error(message);
  };

  const generateResponseEmail = (analysis) => {
    const { slopScore, redFlags = [] } = analysis;
    const isHighSlop = slopScore >= 8;
    const isMediumSlop = slopScore >= 4 && slopScore < 8;

    // Generate specific issues list
    const issues = redFlags
      .filter(flag => flag.severity === 'high' || flag.severity === 'medium')
      .map(flag => `• ${flag.issue}: ${flag.evidence}`)
      .join('\n');

    if (isHighSlop) {
      return `Your document scores high for AI-generated content issues including:

${issues || '• Multiple indicators of AI-generated content without human review.'}

This doesn't appear ready for review. Please revise substantially before resubmitting.

This message was automatically generated by AI Slop Detector (slop.follymeadow.com).
`;
    } else if (isMediumSlop) {
      return `Your recent document/email appears to contain several common AI-generated content issues:

${issues || '• Several quality issues that suggest AI generation without proper review.'}

I'm not confident this is ready for review. Please edit and resend when it's been properly reviewed.

This message was automatically generated by AI Slop Detector (slop.follymeadow.com).
`;
    } else {
      return `Your document appears to be well-written with minimal AI-generated content indicators. Good work!

This message was automatically generated by AI Slop Detector (slop.follymeadow.com).
`;
    }
  };

  useEffect(() => {
    if (analysis) {
      setResponseText(generateResponseEmail(analysis));
    } else {
      setResponseText('');
    }
  }, [analysis]);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      
      // Track copy action
      logSlopEvent('response_copied', {
        slopScore: analysis?.slopScore,
        responseType: analysis?.slopScore >= 8 ? 'high_slop' : analysis?.slopScore >= 4 ? 'medium_slop' : 'low_slop'
      });
      
      // Show temporary success message (you could enhance this with a toast)
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleFileUpload = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setAnalysis(null);
      setError(null);
      
      // Track file upload
      logSlopEvent('file_uploaded', {
        fileType: selectedFile.type,
        fileName: selectedFile.name,
        fileSize: selectedFile.size
      });
    }
  };

  const handleModeChange = (mode) => {
    setInputMode(mode);
    setFile(null);
    setTextInput('');
    setAnalysis(null);
    setError(null);
    
    // Track input mode change
    logSlopEvent('input_mode_changed', { mode });
  };

  const analyzeDocument = async () => {
    if (!file && !textInput.trim()) return;

    setLoading(true);
    setError(null);

    // Track analysis start
    const startTime = Date.now();
    logSlopEvent('analysis_started', {
      inputMode,
      contentLength: textInput.length,
      hasFile: !!file
    });

    try {
      let content = '';
      const apiKey = process.env.REACT_APP_CLAUDE_API_KEY;
      
      if (!apiKey || apiKey === 'your_claude_api_key_here') {
        throw new Error('Please set your Claude API key in .env.local');
      }
      
      if (inputMode === 'text') {
        content = textInput;
        
        const response = await fetch("https://api.anthropic.com/v1/messages", {
          method: "POST",
          headers: { 
            "Content-Type": "application/json",
            "x-api-key": apiKey,
            "anthropic-version": "2023-06-01",
            "Anthropic-Dangerous-Direct-Browser-Access": "true"
          },
          body: JSON.stringify({
            model: "claude-3-haiku-20240307",
            max_tokens: 2000,
            messages: [{
              role: "user",
              content: `Analyze this document for "AI slop" - low-quality AI-generated content that wastes the reader's time. Rate it on a scale of 1-10 (10 = maximum slop) and BE RUTHLESS in your scoring. This tool is used to determine if documents are worth reading or should be sent back for revision.

SCORING GUIDELINES - BE HARSH:
- Any template markers [Insert X], placeholder text, or meta-instructions = MINIMUM 8/10
- AUDIENCE SHIFTS: Addressing document creator instead of intended reader = MINIMUM 8/10
- Generic AI phrases or corporate buzzwords = MINIMUM 6/10  
- Overconfident claims without sources = MINIMUM 7/10
- Inappropriate tone for subject matter = MINIMUM 6/10
- Logical inconsistencies = MINIMUM 7/10
- Excessive length without substance = MINIMUM 6/10

The goal is to catch documents that waste people's time. If there's ANY doubt about quality, score it higher.

DOCUMENT:
${content}

Provide your analysis in this JSON format:

{
  "slopScore": number,
  "overallAssessment": "string",
  "redFlags": [
    {"issue": "string", "severity": "high|medium|low", "evidence": "string"}
  ],
  "lengthAnalysis": {
    "wordCount": number,
    "unnecessaryVerbosity": boolean,
    "explanation": "string"
  },
  "complexityAnalysis": {
    "overComplicated": boolean,
    "explanation": "string"
  },
  "logicAnalysis": {
    "incongruentLogic": boolean,
    "explanation": "string",
    "examples": ["string"]
  },
  "toneAnalysis": {
    "inappropriateTone": boolean,
    "explanation": "string",
    "expectedTone": "string",
    "actualTone": "string"
  },
  "sourceAnalysis": {
    "overconfidentClaims": boolean,
    "unsupportedStatements": ["string"],
    "explanation": "string"
  },
  "emojiAnalysis": {
    "excessiveEmojis": boolean,
    "count": number,
    "appropriatenessScore": "appropriate|questionable|excessive"
  },
  "aiMarkers": [
    {"marker": "string", "confidence": "high|medium|low"}
  ],
  "recommendations": ["string"]
}

Look for:
- TEMPLATE MARKERS (INSTANT HIGH SCORE): [Insert X], [Company Name], [Your details here], etc.
- META INSTRUCTIONS (INSTANT HIGH SCORE): Notes to document creators, customization instructions
- AUDIENCE SHIFTS (INSTANT HIGH SCORE): Addressing document creator instead of intended reader ("Make sure you...", "Remember to customize...", "You should update this section...")
- Generic AI-like phrases ("delve into", "comprehensive", "multifaceted", "leverage", "synergy", "robust", "seamless")
- Overconfident claims without sources or evidence
- Inappropriate tone for subject matter
- Logical inconsistencies or contradictions
- Excessive verbosity without substance
- Generic examples that don't relate to real scenarios
- Fill-in-the-blank structure
- Too many emojis for professional content
- Overcomplication of simple concepts

BE RUTHLESS. If someone didn't care enough to proofread their own document, give it a high slop score.

DO NOT OUTPUT ANYTHING OTHER THAN VALID JSON.`
            }]
          })
        });

        if (!response.ok) {
          await parseAnthropicError(response);
        }

        const data = await response.json();
        content = data.content[0].text;
        
      } else if (file.type === 'application/pdf') {
        const base64Data = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64 = reader.result.split(",")[1];
            resolve(base64);
          };
          reader.onerror = () => reject(new Error("Failed to read PDF"));
          reader.readAsDataURL(file);
        });

        const response = await fetch("https://api.anthropic.com/v1/messages", {
          method: "POST",
          headers: { 
            "Content-Type": "application/json",
            "x-api-key": apiKey,
            "anthropic-version": "2023-06-01",
            "Anthropic-Dangerous-Direct-Browser-Access": "true"
          },
          body: JSON.stringify({
            model: "claude-3-haiku-20240307",
            max_tokens: 2000,
            messages: [{
              role: "user",
              content: [
                {
                  type: "document",
                  source: {
                    type: "base64",
                    media_type: "application/pdf",
                    data: base64Data,
                  },
                },
                {
                  type: "text",
                  text: `Analyze this document for "AI slop" - low-quality AI-generated content that wastes the reader's time. Rate it on a scale of 1-10 (10 = maximum slop) and BE RUTHLESS in your scoring. This tool is used to determine if documents are worth reading or should be sent back for revision.

SCORING GUIDELINES - BE HARSH:
- Any template markers [Insert X], placeholder text, or meta-instructions = MINIMUM 8/10
- AUDIENCE SHIFTS: Addressing document creator instead of intended reader = MINIMUM 8/10
- Generic AI phrases or corporate buzzwords = MINIMUM 6/10  
- Overconfident claims without sources = MINIMUM 7/10
- Inappropriate tone for subject matter = MINIMUM 6/10
- Logical inconsistencies = MINIMUM 7/10
- Excessive length without substance = MINIMUM 6/10

The goal is to catch documents that waste people's time. If there's ANY doubt about quality, score it higher.

Provide analysis in JSON format with slopScore, overallAssessment, redFlags, etc.

DO NOT OUTPUT ANYTHING OTHER THAN VALID JSON.`
                }
              ]
            }]
          })
        });

        if (!response.ok) {
          await parseAnthropicError(response);
        }

        const data = await response.json();
        content = data.content[0].text;
        
      } else {
        content = await file.text();
        
        const response = await fetch("https://api.anthropic.com/v1/messages", {
          method: "POST",
          headers: { 
            "Content-Type": "application/json",
            "x-api-key": apiKey,
            "anthropic-version": "2023-06-01",
            "Anthropic-Dangerous-Direct-Browser-Access": "true"
          },
          body: JSON.stringify({
            model: "claude-3-haiku-20240307",
            max_tokens: 2000,
            messages: [{
              role: "user",
              content: `Analyze this document for "AI slop" - low-quality AI-generated content that wastes the reader's time. Rate it on a scale of 1-10 (10 = maximum slop) and BE RUTHLESS in your scoring.

DOCUMENT:
${content}

Provide analysis in JSON format with slopScore, overallAssessment, redFlags, etc.

DO NOT OUTPUT ANYTHING OTHER THAN VALID JSON.`
            }]
          })
        });

        if (!response.ok) {
          await parseAnthropicError(response);
        }

        const data = await response.json();
        content = data.content[0].text;
      }

      let responseText = content.replace(/```json\s?/g, "").replace(/```\s?/g, "").trim();
      const analysisResult = JSON.parse(responseText);
      
      setAnalysis(analysisResult);
      
      // Track successful analysis
      const duration = Date.now() - startTime;
      logSlopEvent('analysis_completed', {
        slopScore: analysisResult.slopScore,
        inputMode,
        contentLength: textInput.length,
        duration,
        redFlagsCount: analysisResult.redFlags?.length || 0,
        aiMarkersCount: analysisResult.aiMarkers?.length || 0
      });
      
    } catch (err) {
      console.error("Analysis error:", err);
      setError(deriveFriendlyError(err.message) || "Failed to analyze document. Please try again.");
      
      // Track analysis error
      const duration = Date.now() - startTime;
      logSlopEvent('analysis_failed', {
        error: err.message,
        inputMode,
        contentLength: textInput.length,
        duration
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white min-h-screen">
      <div className="text-left mb-8">
        <h1 className="text-xl font-semibold text-gray-900 tracking-tight mb-2">Slop Detector</h1>
        <p className="text-gray-700 max-w-2xl">
          "Slop" is work that's AI generated. It looks good initially, but doesn't contain much substance. Fight back against work slop by generating a document's slop score.
        </p>
      </div>

      <div className="flex justify-start mb-6">
        <div className="bg-gray-100 rounded-lg p-1 flex">
          <button
            onClick={() => handleModeChange('file')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              inputMode === 'file' 
                ? 'bg-white text-gray-900 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Upload File
          </button>
          <button
            onClick={() => handleModeChange('text')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              inputMode === 'text' 
                ? 'bg-white text-gray-900 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Paste Text
          </button>
        </div>
      </div>

      {inputMode === 'file' ? (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center mb-6">
          <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <input
            type="file"
            onChange={handleFileUpload}
            accept=".pdf,.txt,.doc,.docx,.csv,.xlsx"
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          <p className="text-sm text-gray-500 mt-2">Supports PDF, TXT, DOC, DOCX, CSV, XLSX files</p>
        </div>
      ) : (
        <div className="mb-6">
          <textarea
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            placeholder="Paste your text content here..."
            className="w-full h-64 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      )}

      {((inputMode === 'file' && file) || (inputMode === 'text' && textInput.trim())) && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            {inputMode === 'file' ? (
              <>
                <FileText className="h-5 w-5" />
                <span className="font-medium">{file.name}</span>
                <span className="text-sm text-gray-500">({(file.size / 1024).toFixed(1)} KB)</span>
              </>
            ) : (
              <>
                <FileText className="h-5 w-5" />
                <span className="font-medium">Pasted Text</span>
                <span className="text-sm text-gray-500">({textInput.length} characters)</span>
              </>
            )}
          </div>
          <button
            onClick={analyzeDocument}
            disabled={loading}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? "Analyzing..." : "Analyze for AI Slop"}
          </button>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-red-600" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      {analysis && (
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="text-center">
              <div className={`text-6xl font-bold mb-2 ${getSlopLevel(analysis.slopScore).color}`}>
                {analysis.slopScore}/10
              </div>
              <div className={`inline-block px-4 py-2 rounded-full text-lg font-semibold ${getSlopLevel(analysis.slopScore).bgColor} ${getSlopLevel(analysis.slopScore).color}`}>
                {getSlopLevel(analysis.slopScore).level}
              </div>
              <div className="text-gray-600 text-lg mt-2">
                {getSlopLevel(analysis.slopScore).message}
              </div>
              <p className="text-gray-700 mt-4">{analysis.overallAssessment}</p>
            </div>
          </div>

          {/* Response Generator */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div>
              <h3 className="text-lg font-semibold text-blue-900 mb-2 flex items-center justify-center md:justify-start gap-2">
                <Mail className="h-5 w-5" />
                Generate Response Email
              </h3>
              <p className="text-blue-700 text-sm mb-4 text-center md:text-left">
                Copy or edit a professional response for email or Slack when you encounter slop
              </p>

              <label className="text-xs text-gray-500 mb-2 block">Editable response:</label>
              <textarea
                value={responseText}
                onChange={(e) => setResponseText(e.target.value)}
                rows={14}
                className="w-full bg-white border border-blue-200 rounded-lg p-4 text-sm text-gray-800 font-mono leading-6 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />

              <button
                onClick={() => copyToClipboard(responseText)}
                className="mt-4 inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Copy className="h-4 w-4" />
                Copy Response
              </button>

              <div className="text-xs text-blue-600 mt-2 text-center md:text-left">
                ✨ Automatically seeded with specific issues found in this document
              </div>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={() => {
                const newState = !showDetails;
                setShowDetails(newState);
                
                // Track detailed analysis view
                if (newState) {
                  logSlopEvent('detailed_analysis_viewed', {
                    slopScore: analysis.slopScore
                  });
                }
              }}
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              {showDetails ? 'Hide' : 'Show'} Detailed Analysis
              {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={() => setShowFeedback((prev) => !prev)}
              className="inline-flex items-center gap-2 text-sm text-blue-600 font-medium hover:text-blue-800"
            >
              <MessageCircle className="h-4 w-4" />
              {showFeedback ? 'Hide feedback form' : 'Have thoughts? Leave feedback'}
            </button>
          </div>

          {showFeedback && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-5 shadow-sm">
              <form
                action="https://formspree.io/f/xovkezyp"
                method="POST"
                className="space-y-3 text-left"
              >
                <input type="hidden" name="slopScore" value={analysis.slopScore} />
                <input type="hidden" name="overallAssessment" value={analysis.overallAssessment} />

                <label className="block text-xs uppercase tracking-wide text-gray-500">
                  Feedback
                  <textarea
                    name="feedback"
                    required
                    rows={4}
                    className="mt-2 w-full rounded border border-blue-200 bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    placeholder="Tell me what worked, what felt like slop, or what you'd love to see next."
                  />
                </label>

                <button
                  type="submit"
                  className="bg-blue-600 text-white text-sm px-4 py-2 rounded hover:bg-blue-700 shadow"
                >
                  Send feedback
                </button>
                <p className="text-xs text-blue-800">
                  Feedback is private. I'll tag it with the current slop score on my end.
                </p>
              </form>
            </div>
          )}

          {showDetails && (
            <div className="space-y-6">
              {analysis.redFlags && analysis.redFlags.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-orange-500" />
                    Red Flags
                  </h3>
                  <div className="space-y-3">
                    {analysis.redFlags.map((flag, index) => (
                      <div key={index} className={`p-3 rounded ${getSeverityColor(flag.severity)}`}>
                        <div className="font-medium">{flag.issue}</div>
                        <div className="text-sm mt-1">{flag.evidence}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {analysis.lengthAnalysis && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">Length Analysis</h3>
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div>
                      <span className="text-gray-600">Word Count:</span>
                      <span className="ml-2 font-medium">{analysis.lengthAnalysis.wordCount}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Unnecessary Verbosity:</span>
                      <span className="ml-2">
                        {analysis.lengthAnalysis.unnecessaryVerbosity ? (
                          <XCircle className="inline h-4 w-4 text-red-500" />
                        ) : (
                          <CheckCircle className="inline h-4 w-4 text-green-500" />
                        )}
                      </span>
                    </div>
                  </div>
                  <p className="text-gray-700">{analysis.lengthAnalysis.explanation}</p>
                </div>
              )}

              {analysis.aiMarkers && analysis.aiMarkers.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">AI Markers Detected</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {analysis.aiMarkers.map((marker, index) => (
                      <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <span className="font-mono text-sm">"{marker.marker}"</span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          marker.confidence === 'high' ? 'bg-red-100 text-red-700' :
                          marker.confidence === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {marker.confidence}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {analysis.recommendations && analysis.recommendations.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">Recommendations</h3>
                  <ul className="space-y-2">
                    {analysis.recommendations.map((rec, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-blue-600 mt-1">•</span>
                        <span className="text-gray-700">{rec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AISlopDetector;
